import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiInformationOutline } from '@mdi/js';
import { classApi } from '../../../api/classes';
import { useAuth } from '../../../contexts/AuthContext';
import { toast } from 'react-toastify';
import ClosedSchoolNotice from './ClosedSchoolNotice';
import './PublicSchool.css';
import './PublicEnquirePage.css';

/**
 * PublicEnquirePage - Public-facing class enquiry form
 * URL: /school/:orgSlug/enquire?classId=xxx&className=Salsa+Beginners
 * No auth required. Pre-fills from auth or student lookup.
 */
const PublicEnquirePage = () => {
    const { orgSlug } = useParams();
    const [searchParams] = useSearchParams();
    const { user, accountDetails, loading: authLoading } = useAuth();

    const classId = searchParams.get('classId') || '';
    const className = searchParams.get('className') || '';
    // Short-course role pauses, carried from the school/group page enquire link.
    // When the chosen role is paused, this is a waitlist sign-up, not a direct enquiry.
    const pauseLeaders = searchParams.get('pauseLeaders') === '1';
    const pauseFollowers = searchParams.get('pauseFollowers') === '1';

    const [school, setSchool] = useState(null);
    const [isLoadingSchool, setIsLoadingSchool] = useState(true);

    // Form fields
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [dob, setDob] = useState('');
    const [experienceNotes, setExperienceNotes] = useState('');

    const [role, setRole] = useState(null);
    const [isLookingUp, setIsLookingUp] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const isLoggedIn = !!user;
    const emailRef = useRef(null);

    // At least one role is full on this short course (shown up front, before any role pick).
    const anyRolePaused = pauseLeaders || pauseFollowers;
    // Human label for which role(s) are full, for the up-front banner.
    const pausedRolesLabel =
        pauseLeaders && pauseFollowers
            ? 'Leader and follower places'
            : pauseLeaders
              ? 'Leader places'
              : 'Follower places';
    // The chosen role is paused → submitting joins the waitlist for that role.
    const chosenRolePaused =
        (role === 'leader' && pauseLeaders) || (role === 'follower' && pauseFollowers);
    // Treat it as a waitlist submission when the picked role is paused, OR when both roles
    // are full (any submission is necessarily a waitlist join regardless of role choice).
    const isWaitlist = chosenRolePaused || (pauseLeaders && pauseFollowers && anyRolePaused);

    // Load school branding
    useEffect(() => {
        const load = async () => {
            try {
                const r = await classApi.getPublicSchool(orgSlug);
                if (r.ok) setSchool(r.data.school);
            } catch {
                // branding won't apply — page still works
            } finally {
                setIsLoadingSchool(false);
            }
        };
        load();
    }, [orgSlug]);

    // Pre-fill from logged-in account
    // Note: email lives in the JWT token (user.email), not in accountDetails (profile API excludes it)
    useEffect(() => {
        if (!authLoading && user) {
            setFirstName(accountDetails?.firstName || '');
            setLastName(accountDetails?.lastName || '');
            setEmail(user.email || '');
        }
    }, [authLoading, user, accountDetails]);

    const handleEmailBlur = async () => {
        if (!email || isLoggedIn) return;
        setIsLookingUp(true);
        try {
            const r = await classApi.lookupStudent(orgSlug, email);
            if (r.ok && r.data.found) {
                if (r.data.firstName) setFirstName(r.data.firstName);
                if (r.data.lastName) setLastName(r.data.lastName);
                if (r.data.phone) setPhone(r.data.phone);
            }
        } catch {
            // silently ignore — user just fills in manually
        } finally {
            setIsLookingUp(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!firstName.trim()) { toast.error('First name is required.'); return; }
        if (!lastName.trim()) { toast.error('Last name is required.'); return; }
        if (!isLoggedIn && !email.trim()) { toast.error('Email address is required.'); return; }
        if (!phone.trim()) { toast.error('Phone number is required.'); return; }
        setIsSubmitting(true);
        try {
            const payload = {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim(),
                phone: phone.trim(),
                dob: dob || null,
                classId: classId || null,
                className: className || null,
                accountId: user?.accountId || null,
                experienceNotes: experienceNotes.trim(),
                role: role || null,
                waitlisted: isWaitlist,
            };
            const r = await classApi.submitEnquiry(orgSlug, payload);
            if (r.ok) {
                setSubmitted(true);
            } else {
                toast.error(r.data?.message || 'Something went wrong. Please try again.');
            }
        } catch {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const b = school?.branding || {};
    const headerColor = b.headerColor || '#451f78';
    const headerTextColor = b.headerTextColor || '#ffffff';
    const backgroundColor = b.backgroundColor || '#f5f0fb';
    const textColor = b.textColor || '#333333';
    const buttonColor = b.buttonColor || '#451f78';
    const buttonTextColor = b.buttonTextColor || '#ffffff';

    if (isLoadingSchool || authLoading) {
        return <div className="public-school"><p className="public-school-loading">Loading...</p></div>;
    }

    // Class management switched off by the school: no enquiry form, just the closed notice.
    if (school?.isOpen === false) {
        return <ClosedSchoolNotice orgSlug={orgSlug} orgName={school.name} branding={school.branding} />;
    }

    if (submitted) {
        return (
            <div className="public-school public-enquire" style={{ background: backgroundColor, color: textColor }}>
                <div className="public-school-header public-school-header--detail" style={{ background: headerColor }}>
                    <Link to={`/school/${orgSlug}`} className="public-group-back-link" style={{ color: headerTextColor, opacity: 0.75 }}>
                        {school?.name || 'School'}
                    </Link>
                    <h1 className="public-school-title" style={{ color: headerTextColor }}>
                        {isWaitlist ? 'Added to Waitlist' : 'Enquiry Sent'}
                    </h1>
                </div>
                <div className="public-school-body">
                    <div className="public-enquire-success">
                        <p className="public-enquire-success-title">Thanks, {firstName}!</p>
                        <p className="public-enquire-success-body">
                            {isWaitlist
                                ? `You're on the waitlist${className ? ` for ${className}` : ''}. We'll be in touch if a place opens up.`
                                : `We've received your enquiry${className ? ` about ${className}` : ''} and will be in touch soon.`}
                        </p>
                        <Link
                            to={`/school/${orgSlug}`}
                            className="public-enquire-submit-btn"
                            style={{ background: buttonColor, color: buttonTextColor }}
                        >
                            Back to school
                        </Link>
                    </div>
                </div>
                <p className="public-school-powered-by">Powered by Dance Localé</p>
            </div>
        );
    }

    return (
        <div className="public-school public-enquire" style={{ background: backgroundColor, color: textColor }}>
            <div className="public-school-header public-school-header--detail" style={{ background: headerColor }}>
                <Link to={`/school/${orgSlug}`} className="public-group-back-link" style={{ color: headerTextColor, opacity: 0.75 }}>
                    {school?.name || 'School'}
                </Link>
                <h1 className="public-school-title" style={{ color: headerTextColor }}>
                    {className ? `Enquire about ${className}` : 'General Enquiry'}
                </h1>
            </div>

            <div className="public-school-body">
                {isLoggedIn && (
                    <div className="public-enquire-logged-in-banner">
                        {accountDetails?.avatarUrl ? (
                            <img
                                src={accountDetails.avatarUrl}
                                alt=""
                                className="public-enquire-avatar"
                            />
                        ) : (
                            <div className="public-enquire-avatar public-enquire-avatar--initials">
                                {`${accountDetails?.firstName?.[0] || user?.email?.[0] || '?'}`.toUpperCase()}
                            </div>
                        )}
                        <span>
                            You're signed in as <strong>
                                {accountDetails?.firstName
                                    ? `${accountDetails.firstName}${accountDetails.lastName ? ` ${accountDetails.lastName}` : ''}`
                                    : user?.email}
                            </strong>
                        </span>
                    </div>
                )}

                {anyRolePaused && (
                    <div className="public-enquire-waitlist-notice public-enquire-waitlist-notice--banner">
                        <Icon path={mdiInformationOutline} size={0.9} />
                        <span>
                            {pausedRolesLabel} for this course are currently full.
                            {pauseLeaders && pauseFollowers
                                ? ' You can still add your details below to join the waitlist, and we will be in touch if a place opens up.'
                                : ' You can still add your details below to join the waitlist for that role, and we will be in touch if a place opens up.'}
                        </span>
                    </div>
                )}

                <div className="public-enquire-card">
                    <form onSubmit={handleSubmit} className="public-enquire-form">
                        {!isLoggedIn && (
                            <div className="public-enquire-row">
                                <div className="public-enquire-field">
                                    <label className="public-enquire-label" htmlFor="enq-email">Email <span className="public-enquire-required">*</span></label>
                                    <input
                                        ref={emailRef}
                                        id="enq-email"
                                        type="email"
                                        className="public-enquire-input"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        onBlur={handleEmailBlur}
                                        placeholder="your@email.com"
                                        autoComplete="email"
                                    />
                                    {isLookingUp && <p className="public-enquire-hint">Looking up…</p>}
                                </div>
                                <div className="public-enquire-field">
                                    <label className="public-enquire-label" htmlFor="enq-phone">Phone <span className="public-enquire-required">*</span></label>
                                    <input
                                        id="enq-phone"
                                        type="tel"
                                        className="public-enquire-input"
                                        value={phone}
                                        onChange={e => setPhone(e.target.value)}
                                        placeholder="+61 4xx xxx xxx"
                                        autoComplete="tel"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="public-enquire-row">
                            <div className="public-enquire-field">
                                <label className="public-enquire-label" htmlFor="enq-first">First name <span className="public-enquire-required">*</span></label>
                                <input
                                    id="enq-first"
                                    type="text"
                                    className="public-enquire-input"
                                    value={firstName}
                                    onChange={e => setFirstName(e.target.value)}
                                    placeholder="Jane"
                                    autoComplete="given-name"
                                    required
                                />
                            </div>
                            <div className="public-enquire-field">
                                <label className="public-enquire-label" htmlFor="enq-last">Last name <span className="public-enquire-required">*</span></label>
                                <input
                                    id="enq-last"
                                    type="text"
                                    className="public-enquire-input"
                                    value={lastName}
                                    onChange={e => setLastName(e.target.value)}
                                    placeholder="Smith"
                                    autoComplete="family-name"
                                />
                            </div>
                        </div>

                        {isLoggedIn && (
                            <div className="public-enquire-row">
                            <div className="public-enquire-field">
                                <label className="public-enquire-label" htmlFor="enq-email-ro">Email</label>
                                <input
                                    id="enq-email-ro"
                                    type="email"
                                    className="public-enquire-input public-enquire-input--readonly"
                                    value={email}
                                    readOnly
                                />
                            </div>
                            <div className="public-enquire-field">
                                <label className="public-enquire-label" htmlFor="enq-phone">Phone <span className="public-enquire-required">*</span></label>
                                <input
                                    id="enq-phone"
                                    type="tel"
                                    className="public-enquire-input"
                                    value={phone}
                                    onChange={e => setPhone(e.target.value)}
                                    placeholder="+61 4xx xxx xxx"
                                    autoComplete="tel"
                                />
                            </div>
                            </div>
                        )}


                        <div className="public-enquire-field">
                            <label className="public-enquire-label" htmlFor="enq-dob">Date of birth (optional)</label>
                            <input
                                id="enq-dob"
                                type="text"
                                className="public-enquire-input"
                                value={dob}
                                onChange={e => setDob(e.target.value)}
                                placeholder="DD/MM/YYYY"
                                maxLength={10}
                                autoComplete="bday"
                                style={{ maxWidth: '170px' }}
                            />
                        </div>

                        <div className="public-enquire-field">
                            <label className="public-enquire-label" htmlFor="enq-notes">Experience / notes (optional)</label>
                            <textarea
                                id="enq-notes"
                                className="public-enquire-input public-enquire-textarea"
                                value={experienceNotes}
                                onChange={e => setExperienceNotes(e.target.value)}
                                placeholder="Tell us about your dance experience, any questions, or special requirements…"
                                rows={4}
                            />
                        </div>

                        <div className="public-enquire-field">
                            <label className="public-enquire-label">Are you a leader or follower? (optional)</label>
                            <div className="public-enquire-role-toggle">
                                <button
                                    type="button"
                                    className={`public-enquire-role-btn${role === 'leader' ? ' active-leader' : ''}`}
                                    onClick={() => setRole(role === 'leader' ? null : 'leader')}>
                                    Leader{pauseLeaders ? ' (full)' : ''}
                                </button>
                                <button
                                    type="button"
                                    className={`public-enquire-role-btn${role === null ? ' active-unspecified' : ''}`}
                                    onClick={() => setRole(null)}>
                                    Not specified
                                </button>
                                <button
                                    type="button"
                                    className={`public-enquire-role-btn${role === 'follower' ? ' active-follower' : ''}`}
                                    onClick={() => setRole(role === 'follower' ? null : 'follower')}>
                                    Follower{pauseFollowers ? ' (full)' : ''}
                                </button>
                            </div>
                        </div>

                        {isWaitlist && (
                            <div className="public-enquire-waitlist-notice">
                                <Icon path={mdiInformationOutline} size={0.85} />
                                <span>
                                    {chosenRolePaused
                                        ? `You're joining the waitlist as a ${role === 'leader' ? 'leader' : 'follower'}.`
                                        : "You're joining the waitlist for this course."}
                                </span>
                            </div>
                        )}

                        <button
                            type="submit"
                            className="public-enquire-submit-btn"
                            style={{ background: buttonColor, color: buttonTextColor }}
                            disabled={isSubmitting}
                        >
                            {isSubmitting
                                ? 'Sending…'
                                : isWaitlist
                                  ? 'Join waitlist'
                                  : 'Send enquiry'}
                        </button>
                    </form>
                </div>
            </div>
            <p className="public-school-powered-by">Powered by Dance Localé</p>
        </div>
    );
};

export default PublicEnquirePage;
